/*
 * About v2 page interactions. Each behaviour is a custom element so it
 * initialises per section, survives theme editor re-renders and is safe to
 * load more than once.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Background YouTube video: loads when near the viewport, stays behind the
     poster until playback starts, pauses while off screen. */
  class Av2Video extends HTMLElement {
    connectedCallback() {
      this.videoId = this.dataset.youtubeId;
      if (!this.videoId || reducedMotion.matches) return;

      this.origin = 'https://www.youtube-nocookie.com';
      this.onMessage = this.onMessage.bind(this);
      this.observer = new IntersectionObserver((entries) => this.onIntersect(entries[0]), {
        rootMargin: '200px 0px',
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('message', this.onMessage);
      clearTimeout(this.revealTimer);
    }

    onIntersect(entry) {
      if (entry.isIntersecting && !this.iframe) this.load();
      if (!this.iframe) return;
      this.command(entry.isIntersecting ? 'playVideo' : 'pauseVideo');
    }

    load() {
      const params = new URLSearchParams({
        autoplay: '1',
        mute: '1',
        loop: '1',
        playlist: this.videoId,
        controls: '0',
        playsinline: '1',
        rel: '0',
        modestbranding: '1',
        iv_load_policy: '3',
        disablekb: '1',
        enablejsapi: '1',
        origin: window.location.origin,
      });
      const iframe = document.createElement('iframe');
      iframe.className = 'av2-video__frame';
      iframe.src = `${this.origin}/embed/${this.videoId}?${params}`;
      iframe.title = this.dataset.title || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.tabIndex = -1;
      iframe.setAttribute('aria-hidden', 'true');
      iframe.addEventListener('load', () => this.listen(), { once: true });

      window.addEventListener('message', this.onMessage);
      this.iframe = iframe;
      this.append(iframe);
      // Reveal anyway if the player never reports its state.
      this.revealTimer = setTimeout(() => this.reveal(), 6000);
    }

    listen() {
      this.post({ event: 'listening', id: this.videoId, channel: 'widget' });
    }

    command(func) {
      this.post({ event: 'command', func, args: [] });
    }

    post(message) {
      this.iframe?.contentWindow?.postMessage(JSON.stringify(message), this.origin);
    }

    onMessage(event) {
      if (event.origin !== this.origin || event.source !== this.iframe?.contentWindow) return;
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (error) {
        return;
      }
      const state = data?.info?.playerState ?? (data?.event === 'onStateChange' ? data.info : undefined);
      if (state === 1) this.reveal();
    }

    reveal() {
      clearTimeout(this.revealTimer);
      this.classList.add('is-playing');
    }
  }

  /* Mission gallery. Desktop: the image under the pointer (or focused/tapped)
     widens. Mobile carousel: cards rise in on entry, the card centred in the
     scroller is highlighted, and tapping a side card scrolls it to the centre. */
  class Av2Mission extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-av2-mission-item]')];
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.items.forEach((item) => {
        item.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && !this.mobile.matches) this.activate(item);
        }, { signal });
        item.addEventListener('focus', () => this.activate(item), { signal });
        item.addEventListener('click', () => {
          this.activate(item);
          if (this.mobile.matches) this.centre(item);
        }, { signal });
      });

      this.setupReveal();
      this.mobile.addEventListener('change', () => this.setupCarousel(), { signal });
      this.setupCarousel();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.revealObserver?.disconnect();
      this.carouselObserver?.disconnect();
    }

    setupReveal() {
      if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
      this.setAttribute('data-reveal', '');
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          this.classList.add('is-inview');
          this.revealObserver.disconnect();
        },
        { threshold: 0.25 }
      );
      this.revealObserver.observe(this);
    }

    setupCarousel() {
      this.carouselObserver?.disconnect();
      if (!this.mobile.matches || !('IntersectionObserver' in window)) return;
      // start on the card chosen in the section settings, not the first one
      const initial = this.items.find((item) => item.classList.contains('is-active'));
      if (initial) this.scrollLeft = initial.offsetLeft - (this.clientWidth - initial.offsetWidth) / 2;
      const ratios = new Map();
      this.carouselObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => ratios.set(entry.target, entry.intersectionRatio));
          let best = null;
          ratios.forEach((ratio, item) => {
            if (!best || ratio > ratios.get(best)) best = item;
          });
          if (best && ratios.get(best) > 0.6) this.activate(best);
        },
        { root: this, threshold: [0, 0.25, 0.5, 0.6, 0.75, 0.9, 1] }
      );
      this.items.forEach((item) => this.carouselObserver.observe(item));
    }

    centre(item) {
      const left = item.offsetLeft - (this.clientWidth - item.offsetWidth) / 2;
      this.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    activate(target) {
      this.items.forEach((item) => {
        const active = item === target;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
    }
  }

  /* Showroom switcher: picking a location crossfades the stage and marks
     its thumbnail as current. */
  class Av2Showroom extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-av2-showroom-slide]')];
      this.options = [...this.querySelectorAll('[data-av2-showroom-option]')];
      this.status = this.querySelector('[data-av2-showroom-status]');
      this.listeners = new AbortController();
      this.options.forEach((option) => {
        option.addEventListener('click', () => this.select(Number(option.dataset.index)), {
          signal: this.listeners.signal,
        });
      });
    }

    disconnectedCallback() {
      this.listeners?.abort();
    }

    select(index) {
      this.slides.forEach((slide) => {
        const active = Number(slide.dataset.index) === index;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('aria-hidden', !active);
        if (active) {
          slide.querySelector('img')?.setAttribute('loading', 'eager');
          if (this.status) this.status.textContent = slide.dataset.name || '';
        }
      });
      this.options.forEach((option) => option.setAttribute('aria-pressed', String(Number(option.dataset.index) === index)));
    }
  }

  /* Generative score for the film banner, built with the Web Audio API: an
     ambient pad that changes chord with each scene, plus a fly-through whoosh,
     a landing swell, soft transition swishes, bells and posture ticks. Every
     cue takes a start time, so the same class drives live playback and the
     offline render of the video soundtrack. */
  class Av2Sound {
    // One chord per scene (Hz): Dmaj9, Bm11, Gmaj7#11, A6/9
    static CHORDS = [
      [146.83, 220.0, 329.63, 369.99],
      [123.47, 146.83, 220.0, 329.63],
      [98.0, 146.83, 185.0, 277.18],
      [110.0, 164.81, 246.94, 369.99],
    ];

    constructor(ctx, { musicElement } = {}) {
      this.ctx = ctx;
      this.voices = [];
      const c = ctx;

      this.master = c.createGain();
      this.master.gain.value = 0;
      const compressor = c.createDynamicsCompressor();
      compressor.threshold.value = -20;
      compressor.ratio.value = 3;
      this.master.connect(compressor).connect(c.destination);

      this.dry = c.createGain();
      this.dry.connect(this.master);
      this.reverb = c.createConvolver();
      this.reverb.buffer = this.impulse(3.4);
      const wet = c.createGain();
      wet.gain.value = 0.6;
      this.reverb.connect(wet).connect(this.master);
      this.noise = this.noiseBuffer(3);

      // Pad bus: gentle low-pass that breathes slowly
      this.pad = c.createGain();
      this.pad.gain.value = musicElement ? 0 : 1;
      const tone = c.createBiquadFilter();
      tone.type = 'lowpass';
      tone.frequency.value = 1100;
      tone.Q.value = 0.3;
      const lfo = c.createOscillator();
      const depth = c.createGain();
      lfo.frequency.value = 0.06;
      depth.gain.value = 350;
      lfo.connect(depth).connect(tone.frequency);
      lfo.start();
      this.pad.connect(tone);
      tone.connect(this.dry);
      tone.connect(this.reverb);

      if (musicElement) {
        const music = c.createMediaElementSource(musicElement);
        const level = c.createGain();
        level.gain.value = 0.7;
        music.connect(level).connect(this.master);
        this.hasMusic = true;
      }
    }

    static live(options) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      return AudioCtx ? new Av2Sound(new AudioCtx(), options) : null;
    }

    impulse(seconds) {
      const rate = this.ctx.sampleRate;
      const buffer = this.ctx.createBuffer(2, Math.floor(rate * seconds), rate);
      for (let ch = 0; ch < 2; ch++) {
        const data = buffer.getChannelData(ch);
        for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / data.length, 3);
      }
      return buffer;
    }

    noiseBuffer(seconds) {
      const rate = this.ctx.sampleRate;
      const buffer = this.ctx.createBuffer(1, Math.floor(rate * seconds), rate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      return buffer;
    }

    at(offset = 0) {
      return this.ctx.currentTime + offset;
    }

    fade(level, when = this.at(), seconds = 1.2) {
      const g = this.master.gain;
      g.cancelScheduledValues(when);
      g.setValueAtTime(g.value, when);
      g.linearRampToValueAtTime(level, when + seconds);
    }

    chord(index, when = this.at()) {
      if (this.hasMusic) return;
      const c = this.ctx;
      this.voices.forEach(({ gain, oscillators }) => {
        gain.gain.cancelScheduledValues(when);
        gain.gain.setTargetAtTime(0, when, 0.9);
        oscillators.forEach((osc) => osc.stop(when + 5));
      });
      const notes = Av2Sound.CHORDS[index % Av2Sound.CHORDS.length];
      this.voices = notes.map((freq, i) => {
        const gain = c.createGain();
        gain.gain.setValueAtTime(0, when);
        gain.gain.linearRampToValueAtTime(0.032, when + 2.8);
        gain.connect(this.pad);
        const oscillators = [
          [freq, 'triangle', -4],
          [freq, 'sine', 5],
          [freq * 2, 'sine', 2 + i],
        ].map(([f, type, detune]) => {
          const osc = c.createOscillator();
          osc.type = type;
          osc.frequency.value = f;
          osc.detune.value = detune;
          osc.connect(gain);
          osc.start(when);
          return osc;
        });
        return { gain, oscillators };
      });
    }

    noiseSweep(when, duration, from, to, peak, { type = 'bandpass', q = 1.1, reverb = 0.5 } = {}) {
      const c = this.ctx;
      const source = c.createBufferSource();
      source.buffer = this.noise;
      const filter = c.createBiquadFilter();
      filter.type = type;
      filter.Q.value = q;
      filter.frequency.setValueAtTime(from, when);
      filter.frequency.exponentialRampToValueAtTime(to, when + duration);
      const gain = c.createGain();
      gain.gain.setValueAtTime(0.0001, when);
      gain.gain.exponentialRampToValueAtTime(peak, when + duration * 0.85);
      gain.gain.exponentialRampToValueAtTime(0.0001, when + duration + 0.7);
      source.connect(filter).connect(gain);
      gain.connect(this.dry);
      const send = c.createGain();
      send.gain.value = reverb;
      gain.connect(send).connect(this.reverb);
      source.start(when);
      source.stop(when + duration + 0.8);
    }

    tone(freq, when, peak, decay, { type = 'sine', reverb = 0.8, glideTo } = {}) {
      const c = this.ctx;
      const osc = c.createOscillator();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, when);
      if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, when + decay);
      const gain = c.createGain();
      gain.gain.setValueAtTime(0.0001, when);
      gain.gain.exponentialRampToValueAtTime(peak, when + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, when + decay);
      osc.connect(gain);
      gain.connect(this.dry);
      const send = c.createGain();
      send.gain.value = reverb;
      gain.connect(send).connect(this.reverb);
      osc.start(when);
      osc.stop(when + decay + 0.05);
    }

    whoosh(when, duration) {
      this.noiseSweep(when, duration, 220, 5200, 0.22, { q: 0.9, reverb: 0.35 });
      this.tone(55, when, 0.05, duration + 0.4, { glideTo: 110, reverb: 0.2 });
    }

    land(when) {
      this.tone(62, when, 0.34, 2.2, { glideTo: 38, reverb: 0.3 });
      this.noiseSweep(when, 0.35, 1800, 300, 0.12, { type: 'lowpass', q: 0.5, reverb: 0.9 });
      this.bell(Av2Sound.CHORDS[0][3] * 2, when + 0.05);
    }

    swish(when) {
      this.noiseSweep(when, 0.9, 2600, 500, 0.07, { q: 0.7, reverb: 0.9 });
    }

    bell(freq, when) {
      this.tone(freq, when, 0.07, 4.2, { reverb: 1 });
      this.tone(freq * 2.01, when, 0.018, 2.4, { reverb: 1 });
    }

    tick(freq, when) {
      this.tone(freq, when, 0.035, 0.5, { reverb: 1.1 });
    }

    sceneCue(index, when) {
      const notes = Av2Sound.CHORDS[index % Av2Sound.CHORDS.length];
      this.swish(when);
      this.bell(notes[3] * 2, when + 0.25);
      this.chord(index, when);
    }

    poseCue(index, when) {
      const notes = Av2Sound.CHORDS[1];
      this.tick(notes[index % notes.length] * 4, when);
    }
  }

  /* Film banner: optional wordmark opening, then auto-advancing scenes with a
     progress bar per scene, pause/play, and posture art that sharpens in turn.
     Pauses off screen and in background tabs; reduced motion gets a still. */
  class Av2Cinema extends HTMLElement {
    static Sound = Av2Sound;

    connectedCallback() {
      this.scenes = [...this.querySelectorAll('[data-av2-scene]')];
      this.bars = [...this.querySelectorAll('[data-av2-cine-go]')];
      this.current = this.querySelector('[data-av2-cine-current]');
      this.toggleButton = this.querySelector('[data-av2-cine-toggle]');
      this.soundButton = this.querySelector('[data-av2-cine-sound]');
      this.soundOn = false;
      this.duration = (Number(this.dataset.duration) || 6) * 1000;
      this.style.setProperty('--av2-cine-duration', `${this.duration}ms`);
      this.index = 0;
      this.elapsed = 0;
      this.visible = true;
      this.userPaused = reducedMotion.matches;
      this.tick = this.tick.bind(this);
      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.bars.forEach((bar) => bar.addEventListener('click', () => this.go(Number(bar.dataset.av2CineGo)), { signal }));
      this.toggleButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.sync();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.soundButton?.addEventListener('click', () => this.toggleSound(), { signal });
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.2 });
      this.observer.observe(this);

      this.go(0, true);
      if (this.hasAttribute('data-intro') && !reducedMotion.matches) {
        this.playIntro();
      } else {
        this.classList.remove('is-intro');
      }
      this.sync();
      this.last = performance.now();
      this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.introFrame);
      this.observer?.disconnect();
      this.listeners?.abort();
      this.music?.pause();
      this.sound?.ctx.close();
      this.sound = null;
    }

    get playing() {
      return !this.userPaused && this.visible && !document.hidden && !this.classList.contains('is-intro');
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      if (this.soundOn && !this.sound) {
        const src = this.soundButton?.dataset.audioSrc;
        if (src) {
          this.music = new Audio(src);
          this.music.loop = true;
          this.music.crossOrigin = 'anonymous';
        }
        this.sound = Av2Sound.live({ musicElement: this.music });
        this.sound?.chord(this.index);
      }
      this.sound?.ctx.resume();
      if (this.soundButton) {
        this.soundButton.setAttribute('aria-pressed', String(this.soundOn));
        const label = this.soundOn ? this.soundButton.dataset.labelOff : this.soundButton.dataset.labelOn;
        if (label) this.soundButton.setAttribute('aria-label', label);
      }
      this.audible = undefined;
      this.syncSound();
    }

    /* Sound follows playback: it fades out while paused, off screen or in a
       background tab, and back in when the banner resumes. */
    syncSound() {
      if (!this.sound) return;
      const audible = this.soundOn && this.playing;
      if (audible === this.audible) return;
      this.audible = audible;
      this.sound.fade(audible ? 0.9 : 0, this.sound.at(), audible ? 1.2 : 0.6);
      if (this.music) {
        if (audible) this.music.play().catch(() => {});
        else setTimeout(() => !this.audible && this.music.pause(), 700);
      }
    }

    sync() {
      this.syncSound();
      this.classList.toggle('is-paused', this.userPaused);
      if (this.toggleButton) {
        const label = this.userPaused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
        if (label) this.toggleButton.setAttribute('aria-label', label);
      }
    }

    go(next, immediate = false) {
      const count = this.scenes.length;
      if (!count) return;
      next = ((next % count) + count) % count;
      const previous = this.scenes[this.index];
      if (!immediate && next !== this.index && previous) {
        previous.classList.remove('is-active');
        previous.classList.add('is-leaving');
        previous.setAttribute('aria-hidden', 'true');
        clearTimeout(previous.leaveTimer);
        previous.leaveTimer = setTimeout(() => previous.classList.remove('is-leaving'), 1300);
      }
      this.index = next;
      this.elapsed = 0;
      this.poseIndex = -1;
      const scene = this.scenes[next];
      clearTimeout(scene.leaveTimer);
      scene.classList.remove('is-leaving');
      scene.classList.add('is-active');
      scene.removeAttribute('aria-hidden');
      this.poses = [...scene.querySelectorAll('[data-av2-pose]')];

      this.bars.forEach((bar, i) => {
        bar.classList.toggle('is-current', i === next);
        bar.style.setProperty('--av2-p', i < next ? 1 : 0);
        if (i === next) bar.setAttribute('aria-current', 'true');
        else bar.removeAttribute('aria-current');
      });
      if (this.current) this.current.textContent = String(next + 1).padStart(2, '0');
      if (!immediate && this.audible) this.sound.sceneCue(next, this.sound.at());
      this.updatePoses();
    }

    tick(now) {
      const dt = Math.min(now - this.last, 100);
      this.last = now;
      this.syncSound();
      if (this.playing) {
        this.elapsed += dt;
        this.bars[this.index]?.style.setProperty('--av2-p', Math.min(this.elapsed / this.duration, 1));
        this.updatePoses();
        if (this.elapsed >= this.duration) this.go(this.index + 1);
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    updatePoses() {
      if (!this.poses?.length) return;
      const slot = this.duration / this.poses.length;
      const index = Math.min(Math.floor(this.elapsed / slot), this.poses.length - 1);
      const sharp = this.elapsed - index * slot > Math.min(500, slot * 0.3) || reducedMotion.matches;
      this.poses.forEach((pose, i) => {
        const wasSharp = pose.classList.contains('is-sharp');
        pose.classList.toggle('is-current', i === index);
        pose.classList.toggle('is-sharp', i === index && sharp);
        if (i === index && sharp && !wasSharp && this.audible) this.sound.poseCue(i, this.sound.at());
      });
    }

    /* Clip the photo to the wordmark, then zoom the clip path through the "I"
       until the letter covers the frame. The path is scaled as vector data, so
       even a very large zoom stays cheap to render. */
    playIntro() {
      const intro = this.querySelector('.av2-cine__intro');
      const media = intro?.querySelector('.av2-cine__intro-media');
      const path = intro?.querySelector('[data-av2-wordmark]');
      if (!intro || !media || !path) {
        this.classList.remove('is-intro');
        return;
      }
      const image = media.querySelector('img');
      const WORD_W = 354.111;
      const I_CX = 81.477; // centre of the "I" stem
      const I_HALF = 9.477; // half the stem width
      const I_CY = 35.864;
      const fadeIn = 900;
      const hold = 700;
      const zoom = 2200;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      let start;

      const frame = (now) => {
        start ??= now;
        const t = now - start;
        const width = this.clientWidth;
        const height = this.clientHeight;
        const startScale = Math.min(width * (width < 750 ? 0.72 : 0.56), 780) / WORD_W;
        const anchorX = (width - WORD_W * startScale) / 2 + I_CX * startScale;
        const anchorY = height / 2;
        const endScale = (1.15 * Math.max(anchorX, width - anchorX)) / I_HALF;

        let scale;
        let imageScale = 1.12;
        if (t < fadeIn) {
          const p = t / fadeIn;
          media.style.opacity = String(p);
          scale = startScale * (0.94 + 0.06 * (1 - Math.pow(1 - p, 3)));
        } else if (t < fadeIn + hold) {
          media.style.opacity = '1';
          scale = startScale;
        } else {
          const p = Math.min((t - fadeIn - hold) / zoom, 1);
          const e = ease(p);
          scale = startScale * Math.pow(endScale / startScale, e);
          imageScale = 1.12 - 0.12 * e;
        }
        path.setAttribute('transform', `translate(${anchorX - I_CX * scale} ${anchorY - I_CY * scale}) scale(${scale})`);
        if (image) image.style.transform = `scale(${imageScale})`;

        if (t >= fadeIn + hold + zoom) {
          media.style.clipPath = 'none';
          intro.classList.add('is-done');
          this.classList.remove('is-intro');
          this.elapsed = 0;
          return;
        }
        this.introFrame = requestAnimationFrame(frame);
      };
      this.introFrame = requestAnimationFrame(frame);
    }
  }

  /* Brand film banner: muted, looping video that loads and plays only while
     on screen, with sound and play/pause toggles and a thin progress line. */
  class Av2Film extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.soundButton = this.querySelector('[data-av2-film-sound]');
      this.playButton = this.querySelector('[data-av2-film-play]');
      this.bar = this.querySelector('.av2-film__progress > span');
      // Muted loop plays by default for everyone; the pause button stays available.
      this.userPaused = false;
      this.visible = false;
      this.listeners = new AbortController();
      const { signal } = this.listeners;

      this.playButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.sync();
      }, { signal });
      this.soundButton?.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        if (!this.video.muted) this.userPaused = false;
        this.sync();
      }, { signal });
      this.video.addEventListener('timeupdate', () => {
        if (this.bar && this.video.duration) this.bar.style.transform = `scaleX(${this.video.currentTime / this.video.duration})`;
      }, { signal });
      ['play', 'pause'].forEach((type) => this.video.addEventListener(type, () => this.render(), { signal }));
      document.addEventListener('visibilitychange', () => this.sync(), { signal });

      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      }, { threshold: 0.25 });
      this.observer.observe(this);
      // Phones get the 9:16 cut: swap the poster now, the source on load.
      this.mobile = window.matchMedia('(max-width: 749px)').matches && Boolean(this.video.dataset.srcMobile);
      this.classList.toggle('is-portrait', this.mobile);
      if (this.mobile && this.video.dataset.posterMobile) this.video.poster = this.video.dataset.posterMobile;
      this.tuckUnderHeader();
      // Wide desktop banners (shorter than 16:9) get the 2.4:1 cut, so cover crops less.
      // The inline script in the section already chose the cut (and loaded its placeholder)
      this.wide = this.dataset.cut
        ? this.dataset.cut === 'wide' && Boolean(this.video.dataset.srcWide)
        : !this.mobile && Boolean(this.video.dataset.srcWide) && this.clientHeight > 0 && this.clientWidth / this.clientHeight > 2.1;
      if (this.wide && this.video.dataset.posterWide) this.video.poster = this.video.dataset.posterWide;
      // Reveal the video over the placeholder once it is really showing frames
      const reveal = () => {
        if (this.video.currentTime > 0) {
          this.classList.add('is-started');
          this.video.removeEventListener('timeupdate', reveal);
        }
      };
      this.video.addEventListener('timeupdate', reveal, { signal: this.listeners.signal });
      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.headerObserver?.disconnect();
      this.topObserver?.disconnect();
      this.menuObserver?.disconnect();
      this.topMarker?.remove();
      this.listeners?.abort();
      document.documentElement.classList.remove('av2-film-under-header', 'av2-header-transparent', 'av2-header-on-light');
    }

    /* shopify.com-style hero: when the film opens the page, it slides up behind
       the sticky store header. At the top of the page the header is see-through
       over the film; once the page scrolls it gets its own background back. */
    tuckUnderHeader() {
      const header = document.querySelector('.section-header');
      const section = this.closest('.shopify-section');
      if (!header || !section || section.parentElement?.querySelector('.shopify-section') !== section) return;
      const root = document.documentElement;
      root.classList.add('av2-film-under-header');
      const measure = () => {
        root.style.setProperty('--av2-header-h', `${header.offsetHeight}px`);
        root.style.setProperty('--av2-film-top', `${Math.max(0, section.getBoundingClientRect().top + window.scrollY)}px`);
      };
      measure();
      this.headerObserver = new ResizeObserver(measure);
      this.headerObserver.observe(header);
      // See-through only while the page sits at the very top: a 1px
      // marker at the top of the document is observed, so no scroll handler
      // runs while the page moves.
      this.topMarker = document.createElement('div');
      this.topMarker.setAttribute('aria-hidden', 'true');
      // display is explicit: Dawn hides empty divs (div:empty { display: none })
      this.topMarker.style.cssText = 'display:block;position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
      document.body.prepend(this.topMarker);
      // The bar is see-through only at the very top and while no menu is in
      // use: hovering an item that has a dropdown, or any open dropdown,
      // drawer or search panel, brings the store's header colour back.
      let atTop = window.scrollY < 1;
      let menuHover = false;
      const hasDropdown = (el) => el?.closest?.('.header__inline-menu li')?.querySelector('details, .h2m-submenu--root, .mega-menu__content, .header__submenu');
      const update = () => {
        // Dawn menus, drawer and search use <details>; the Hyper Menu app (US)
        // marks its open top-level item with .h2m-menu-active
        const menuOpen = Boolean(header.querySelector('details[open], .header__inline-menu li.h2m-menu-active'));
        root.classList.toggle('av2-header-transparent', atTop && !menuHover && !menuOpen);
      };
      this.updateHeader = update;
      update();
      this.topObserver = new IntersectionObserver((entries) => {
        atTop = entries[entries.length - 1].isIntersecting;
        update();
        this.sampleHeader?.();
      });
      this.topObserver.observe(this.topMarker);
      const { signal } = this.listeners;
      header.addEventListener('pointerover', (e) => { menuHover = Boolean(hasDropdown(e.target)); update(); }, { signal });
      header.addEventListener('pointerleave', () => { menuHover = false; update(); }, { signal });
      // details toggle does not bubble; listen in the capture phase
      header.addEventListener('toggle', update, { capture: true, signal });
      // app menus open by class, not <details>
      this.menuObserver = new MutationObserver(update);
      header.querySelectorAll('.header__inline-menu').forEach((nav) => this.menuObserver.observe(nav, { subtree: true, attributes: true, attributeFilter: ['class'] }));
      // Read the strip of video behind the header and switch the see-through
      // header to dark links while that strip is light. Works for any video;
      // if the frame cannot be read (cross-origin), the top shade stays.
      const probe = document.createElement('canvas');
      probe.width = 48;
      probe.height = 4;
      const pctx = probe.getContext('2d', { willReadFrequently: true });
      let light = false;
      let blocked = false;
      const sample = () => {
        const v = this.video;
        if (blocked || !root.classList.contains('av2-header-transparent') || v.readyState < 2 || !v.videoWidth) return;
        // map the header's band on screen back to the source frame (object-fit: cover)
        const w = this.clientWidth;
        const h = this.clientHeight;
        const scale = Math.max(w / v.videoWidth, h / v.videoHeight);
        const offX = (w - v.videoWidth * scale) / 2;
        const offY = (h - v.videoHeight * scale) / 2;
        const band = header.offsetHeight;
        try {
          pctx.drawImage(v, -offX / scale, -offY / scale, w / scale, band / scale, 0, 0, probe.width, probe.height);
          const px = pctx.getImageData(0, 0, probe.width, probe.height).data;
          let sum = 0;
          for (let i = 0; i < px.length; i += 4) sum += 0.2126 * px[i] + 0.7152 * px[i + 1] + 0.0722 * px[i + 2];
          const luma = sum / (px.length / 4) / 255;
          // hysteresis so mid-tones do not flicker
          light = light ? luma > 0.45 : luma > 0.58;
          root.classList.toggle('av2-header-on-light', light);
        } catch (e) {
          blocked = true;
        }
      };
      this.sampleHeader = sample;
      ['timeupdate', 'seeked', 'loadeddata'].forEach((type) => this.video.addEventListener(type, sample, { signal: this.listeners.signal }));
    }

    load() {
      if (this.loaded) return;
      this.loaded = true;
      const { srcDesktop, srcMobile, srcWide, av1Desktop, av1Mobile, av1Wide } = this.video.dataset;
      if (srcDesktop) {
        const mp4 = this.mobile ? srcMobile : this.wide ? srcWide : srcDesktop;
        const av1 = this.mobile ? av1Mobile : this.wide ? av1Wide : av1Desktop;
        // AV1 is about 40% smaller; Safari only decodes it with hardware support
        const canAv1 = av1 && this.video.canPlayType('video/webm; codecs="av01.0.08M.10"') === 'probably';
        if (canAv1) {
          this.video.addEventListener('error', () => {
            if (this.video.currentSrc.endsWith('.webm') || this.video.src.includes('.webm')) {
              this.video.src = mp4;
              this.sync();
            }
          }, { once: true, signal: this.listeners.signal });
        }
        this.video.src = canAv1 ? av1 : mp4;
        // The first play starts on the placeholder's frame; loops start from 0
        const start = parseFloat(this.video.dataset.start) || 0;
        if (start) {
          this.video.addEventListener('loadedmetadata', () => {
            if (this.video.currentTime < start) this.video.currentTime = start;
          }, { once: true, signal: this.listeners.signal });
        }
      }
      this.video.preload = 'auto';
    }

    sync() {
      const shouldPlay = this.visible && !this.userPaused && !document.hidden;
      if (shouldPlay) {
        this.load();
        this.video.play().catch(() => {
          // Autoplay refused (e.g. data saver): wait for the play button.
          this.userPaused = true;
          this.render();
        });
      } else if (!this.video.paused) {
        this.video.pause();
      }
      this.render();
    }

    render() {
      const paused = this.video.paused;
      this.classList.toggle('is-paused', paused);
      this.classList.toggle('is-muted', this.video.muted);
      const play = this.playButton;
      if (play) play.setAttribute('aria-label', paused ? play.dataset.labelPlay : play.dataset.labelPause);
      const sound = this.soundButton;
      if (sound) {
        sound.setAttribute('aria-pressed', String(!this.video.muted));
        sound.setAttribute('aria-label', this.video.muted ? sound.dataset.labelOn : sound.dataset.labelOff);
      }
    }
  }

  /* Our story strip: continuous auto-scroll that visitors can also drag or
     swipe. Pauses on mouse hover, keyboard focus and while off screen;
     reduced motion keeps drag but drops the auto-scroll. */
  class Av2StoryStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-av2-story-track]');
      if (!this.track) return;
      const style = getComputedStyle(this.track);
      this.set = parseFloat(style.getPropertyValue('--av2-set')) || 0;
      const duration = parseFloat(style.getPropertyValue('--av2-duration')) || 56;
      if (!this.set) return;
      this.speed = reducedMotion.matches ? 0 : this.set / duration; // px per second
      this.offset = 0;
      this.velocity = 0;
      this.hovered = false;
      this.visible = true;
      this.classList.add('is-js');

      this.listeners = new AbortController();
      const { signal } = this.listeners;
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.hovered = true; }, { signal });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.hovered = false; }, { signal });
      this.addEventListener('pointerdown', (e) => this.dragStart(e), { signal });
      this.addEventListener('pointermove', (e) => this.dragMove(e), { signal });
      ['pointerup', 'pointercancel'].forEach((type) => this.addEventListener(type, (e) => this.dragEnd(e), { signal }));
      // a drag should not also count as a click on a card
      this.addEventListener('click', (e) => { if (this.moved) { e.preventDefault(); e.stopPropagation(); } }, { capture: true, signal });

      this.observer = new IntersectionObserver((entries) => { this.visible = entries[0].isIntersecting; });
      this.observer.observe(this);
      this.last = performance.now();
      this.tick = this.tick.bind(this);
      this.frame = requestAnimationFrame(this.tick);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.listeners?.abort();
    }

    dragStart(e) {
      if (e.button > 0) return;
      this.dragging = { x: e.clientX, offset: this.offset, lastX: e.clientX, lastT: performance.now() };
      this.moved = false;
      this.velocity = 0;
    }

    dragMove(e) {
      const d = this.dragging;
      if (!d) return;
      const dx = e.clientX - d.x;
      if (!this.moved && Math.abs(dx) > 6) {
        this.moved = true;
        this.classList.add('is-dragging');
        this.setPointerCapture?.(e.pointerId);
      }
      if (!this.moved) return;
      const now = performance.now();
      this.velocity = ((e.clientX - d.lastX) / Math.max(now - d.lastT, 1)) * 1000;
      d.lastX = e.clientX;
      d.lastT = now;
      this.offset = d.offset + dx;
      this.render();
    }

    dragEnd(e) {
      if (!this.dragging) return;
      this.dragging = null;
      this.classList.remove('is-dragging');
      if (this.hasPointerCapture?.(e.pointerId)) this.releasePointerCapture(e.pointerId);
      setTimeout(() => { this.moved = false; }, 0);
    }

    tick(now) {
      const dt = Math.min((now - this.last) / 1000, 0.1);
      this.last = now;
      if (!this.dragging && this.visible) {
        // fling momentum decays, then the auto-scroll takes over again
        this.velocity *= Math.pow(0.04, dt);
        if (Math.abs(this.velocity) < 5) this.velocity = 0;
        const focused = this.matches(':focus-within');
        const auto = this.hovered || focused ? 0 : -this.speed;
        this.offset += (this.velocity + auto) * dt;
        this.render();
      }
      this.frame = requestAnimationFrame(this.tick);
    }

    render() {
      const x = ((this.offset % this.set) - this.set) % this.set;
      this.track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`;
    }
  }

  if (!customElements.get('av2-video')) customElements.define('av2-video', Av2Video);
  if (!customElements.get('av2-mission')) customElements.define('av2-mission', Av2Mission);
  if (!customElements.get('av2-showroom')) customElements.define('av2-showroom', Av2Showroom);
  if (!customElements.get('av2-cinema')) customElements.define('av2-cinema', Av2Cinema);
  if (!customElements.get('av2-film')) customElements.define('av2-film', Av2Film);
  if (!customElements.get('av2-story-strip')) customElements.define('av2-story-strip', Av2StoryStrip);
})();
